import { Controller } from "@hotwired/stimulus";

/**
 * Proper Case Controller
 *
 * Title-cases a text input's value on blur. Trims leading/trailing whitespace.
 * Preserves intentional casing:
 *   - Mixed-case words (e.g. "MetLife", "McDonald") are left alone
 *   - Short all-uppercase words ≤ 4 letters (likely acronyms: AIG, USAA, IUL, UL)
 *     are left alone
 *   - All-lowercase or longer all-uppercase words are Title-Cased
 *
 * Word boundaries include whitespace, hyphens, apostrophes, periods, slashes,
 * and ampersands — so "o'brien" → "O'Brien" and "mary-jane" → "Mary-Jane".
 *
 * Usage (single input):
 *   <input type="text"
 *          data-controller="proper-case"
 *          data-action="blur->proper-case#format">
 */
export default class extends Controller {
  format(event) {
    const input = event.target;
    const next = this.properCase(input.value);
    if (next !== input.value) {
      input.value = next;
      input.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  properCase(value) {
    if (!value) return value;
    const trimmed = value.trim();
    if (!trimmed) return "";

    const words = trimmed.split(/\s+/);
    const allShouting = this.isAllShouting(words);

    return words.map((word) => this.properCaseWord(word, allShouting)).join(" ");
  }

  isAllShouting(words) {
    const lettered = words.filter((w) => /[A-Za-z]/.test(w));
    if (lettered.length < 2) return false;
    return lettered.every((w) => /[A-Z]/.test(w) && !/[a-z]/.test(w));
  }

  properCaseWord(word, allShouting) {
    const letters = word.replace(/[^A-Za-z]/g, "");
    if (letters.length === 0) return word;

    const hasUpper = /[A-Z]/.test(letters);
    const hasLower = /[a-z]/.test(letters);

    if (hasUpper && hasLower) return word;
    if (hasUpper && !hasLower && letters.length <= 4 && !allShouting) return word;

    return word
      .toLowerCase()
      .replace(/(^|[\-'./&])(\p{L})/gu, (_match, sep, ch) => sep + ch.toUpperCase());
  }
}
